import { Link } from "react-router-dom";
import { Wrench, Shield, Camera, MessageCircleMore, Star, ArrowRight } from "lucide-react";
import { BrandWord } from "@/components/BrandWord";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#09090B] grain safe-top safe-bottom">
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1760199129864-df2332e3f3e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW9kZXJuJTIwY2FyJTIwZ2FyYWdlfGVufDB8fHx8MTc3NzkyNjMxMXww&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/70 to-transparent" />

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-10 flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#FF3B30] flex items-center justify-center">
              <Wrench className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <BrandWord className="font-heading text-xl font-black tracking-tight" />
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="btn-ghost" data-testid="hero-login">Login</Link>
            <Link to="/login?mode=register" className="btn-primary" data-testid="hero-signup">Get Started</Link>
          </div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 pt-16 pb-32">
          <div className="max-w-4xl">
            <div className="label-eyebrow mb-6">CONFIDENTIAL · ANONYMOUS · PHOTO-DRIVEN</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95] mb-8">
              FIX YOUR CAR<br />
              <span className="text-[#FF3B30]">WITHOUT</span> THE GUESSWORK.
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mb-10 leading-relaxed">
              Snap a photo. Record a quick clip. Stay anonymous. Real shops send private quotes
              and talk to you through the app — not your driveway.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/login?mode=register&role=owner" className="btn-primary flex items-center gap-2" data-testid="cta-owner">
                I'm a Car Owner <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login?mode=register&role=shop" className="btn-secondary flex items-center gap-2" data-testid="cta-shop">
                I'm a Repair Shop <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-24 border-b border-white/10">
        <div className="label-eyebrow mb-6">HOW IT WORKS</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-16 max-w-2xl">
          Three steps between a weird noise and a trusted mechanic.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {[
            { i: "01", t: "UPLOAD ANONYMOUSLY", d: "Shoot a photo or a short video. Describe what's wrong. Your identity never leaves the device.", icon: Camera },
            { i: "02", t: "RECEIVE PRIVATE QUOTES", d: "Vetted shops review your issue and send confidential quotes with timelines and scope.", icon: Shield },
            { i: "03", t: "CHAT & CHOOSE", d: "Talk directly in-app. Compare quotes side-by-side. Accept the one that fits — then rate your shop.", icon: MessageCircleMore },
          ].map((s, idx) => (
            <div key={s.i} className="bg-[#09090B] p-10" data-testid={`how-step-${idx}`}>
              <div className="flex items-start justify-between mb-8">
                <span className="font-mono text-sm text-zinc-600">{s.i}</span>
                <s.icon className="w-6 h-6 text-[#FF3B30]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-bold tracking-tight mb-3">{s.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust bar */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-24 border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-6">WHY MECHANICAR</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-6">
              Built for the paranoid. Priced by the honest.
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Every message, photo, and quote lives behind our private wall. Shops can't cold-call you.
              Owners can't be haggled on the street. Just a real price, from a real technician.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
            {[
              { n: "100%", l: "Anonymous posting" },
              { n: "Local", l: "Nearby shop matching" },
              { n: "5★", l: "Shop rating system" },
              { n: "24/7", l: "In-app messaging" },
            ].map((m) => (
              <div key={m.l} className="bg-[#09090B] p-8">
                <div className="font-heading text-3xl sm:text-4xl font-black text-[#FF3B30] mb-2">{m.n}</div>
                <div className="label-eyebrow text-zinc-500">{m.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-24">
        <div className="border border-white/10 p-10 sm:p-16 bg-[#121214] relative overflow-hidden">
          <Star className="absolute -right-8 -top-8 w-48 h-48 text-[#FF3B30]/10" strokeWidth={1} />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter mb-6 max-w-3xl">
              STOP GUESSING. START FIXING.
            </h2>
            <p className="text-zinc-400 mb-10 max-w-2xl">Your garage just got a quieter waiting room. Join MechaniCar as a car owner or a repair shop.</p>
            <Link to="/login?mode=register" className="btn-primary inline-flex items-center gap-2" data-testid="footer-cta">
              Create Your Account <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-xs text-zinc-600 uppercase tracking-[0.3em]">
        <div>MechaniCar · Private Repair Marketplace</div>
        <div className="mt-3 flex items-center justify-center gap-5 text-zinc-500">
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
          <span className="text-zinc-700">·</span>
          <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
        </div>
      </footer>
    </div>
  );
}
